/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-02-12 14:26:10
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import React, { Component } from 'react';
import { DataTable, Button } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { Field } from 'redux-form';
import memoize from 'memoize-one';

import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { DeleteMajor } from '@shopify/polaris-icons';
import { RenderDatePicker } from 'src/components/views/common/forms/render-date-picker';
import { RenderSelect2CreatableV2 } from 'src/components/views/common/forms/render-select-2-creatable-v2';

import {
  PRODUCT_ATTRIBUTES,
  CONDUCTOR_FOR_PRODUCT_TITLE,
  CONDUCTOR_FOR_PRODUCT_TYPE,
  CONDUCTOR_FOR_PRODUCT_VENDOR,
  CONDUCTOR_FOR_PRODUCT_PRICE,
  CONDUCTOR_FOR_PRODUCT_INVENTORY,
  VALUES_FOR_PRODUCT_INVENTORY,
  CONDUCTOR_FOR_PRODUCT_TAG,
  CONDUCTOR_FOR_DATE
} from 'src/constants/merchandising';
import { isRequired, nonSpecialCharacters, isNumber } from 'src/helpers/form-validation-helper';

const isRequiredValue = isRequired();
const nonSpecialCharactersValue = nonSpecialCharacters();
type Props = {
  field: Object
};

const relationsList = {
  '': [{ label: 'AdminHTML.Merchandising.ConditionsList.relationsList_DefaultItem', value: '' }],
  title: CONDUCTOR_FOR_PRODUCT_TITLE,
  product_type: CONDUCTOR_FOR_PRODUCT_TYPE,
  vendor: CONDUCTOR_FOR_PRODUCT_VENDOR,
  price: CONDUCTOR_FOR_PRODUCT_PRICE,
  inventory: CONDUCTOR_FOR_PRODUCT_INVENTORY,
  tags: CONDUCTOR_FOR_PRODUCT_TAG,
  created_at: CONDUCTOR_FOR_DATE,
  published_at: CONDUCTOR_FOR_DATE
};
let isFormatted = false;

type ComposedProps = Props & WithI18nProps;
// DONE
class ConditionsList extends Component<ComposedProps> {
  getRelationsList = memoize(function (i18n) {
    const res = _.clone(relationsList);
    for (const [key, value] of Object.entries(res)) {
      res[key] = _.map(value, function (item) {
        return {
          ...item,
          label: i18n.translate(item.label)
        };
      });
    }
    return res;
  });
  componentDidMount() {
    if (!isFormatted) {
      isFormatted = true;
    }
  }
  render() {
    const { formValues, i18n } = this.props;
    const conditions = _.get(formValues, 'conditions', []);

    // Render suggestions on table
    const rowConditions = this.buildRows();

    // Render headings
    const headings =
      conditions.length > 1
        ? [
            '',
            i18n.translate('AdminHTML.Merchandising.ConditionsList.heading1'),
            i18n.translate('AdminHTML.Merchandising.ConditionsList.heading2'),
            i18n.translate('AdminHTML.Merchandising.ConditionsList.heading3'),
            ''
          ]
        : [
            '',
            i18n.translate('AdminHTML.Merchandising.ConditionsList.heading1'),
            i18n.translate('AdminHTML.Merchandising.ConditionsList.heading2'),
            i18n.translate('AdminHTML.Merchandising.ConditionsList.heading3')
          ]; // Fix issue tooltip showed in the first column by adding an empty column

    return (
      <div>
        <DataTable
          columnContentTypes={['text', 'text', 'text', 'text']}
          headings={headings}
          rows={rowConditions}
        />
      </div>
    );
  }

  // Render rows for data table
  buildRows = () => {
    const { fields, formValues, i18n } = this.props;
    const conditions = _.get(formValues, 'conditions', []);

    const rows = fields.map((condition, index) => {
      // Get product attribute
      const productAttribute = _.get(conditions, `[${index}].field`, '');

      // Init Relation options based on product attributes
      const relationField = this.initRelationField(condition, productAttribute, index);

      // Init Values field based on product attributes
      const valuesField = this.initValuesField(condition, productAttribute, index);

      // Define remove button
      const removeButton = (
        <Button
          size="slim"
          icon={DeleteMajor}
          onClick={() => {
            fields.remove(index);
          }}
        />
      );

      const row = [
        '',
        <Field
          name={`${condition}.field`}
          component={RenderSelect}
          options={PRODUCT_ATTRIBUTES.map(function (attribute) {
            return {
              ...attribute,
              label: i18n.translate(attribute.label)
            };
          })}
          dataType="string"
          onChange={(e, value, oldValue) => this.changeProductAttribute(value, oldValue, index)}
        />,
        relationField,
        valuesField
      ]; // Fix issue tooltip showed in the first column by adding an empty column

      // Add remove button as last column
      if (conditions.length > 1) {
        row.push(removeButton);
      }

      return row;
    });

    return rows;
  };

  // Create Relation field based on selected product attribute
  initRelationField = (condition, productAttribute, index) => {
    const { i18n } = this.props;
    return (
      <Field
        name={`${condition}.relation`}
        component={RenderSelect}
        options={this.getRelationsList(i18n)[productAttribute]}
        dataType="string"
      />
    );
  };

  // Create Values field based on selected product attribute
  initValuesField = (condition, productAttribute, index) => {
    const { formValues, i18n } = this.props;
    const cond = _.get(formValues, `conditions[${index}]`, []);

    const disabled = !productAttribute ? true : false;
    const isPrice = productAttribute === 'price';

    const textField = (
      <Field
        name={`${condition}.value`}
        type={isPrice ? 'number' : 'text'}
        placeholder=""
        component={RenderTextField}
        validate={
          isPrice
            ? [isNumber, nonSpecialCharactersValue]
            : [isRequiredValue, nonSpecialCharactersValue]
        }
        anotherProps={{ disabled }}
      />
    );

    const selectField = (
      <Field
        name={`${condition}.value`}
        component={RenderSelect}
        options={VALUES_FOR_PRODUCT_INVENTORY.map(function (value) {
          return {
            ...value,
            label: i18n.translate(value.label)
          };
        })}
        dataType="string"
      />
    );

    const datePickerField = (
      <Field
        name={`${condition}.value`}
        index={index}
        component={RenderDatePicker}
        onChooseDate={this.onChooseDate}
        start={cond.value}
        end={cond.value}
        validate={[isRequiredValue]}
      />
    );

    const select2CreatableFieldProductType = (
      <Field
        name={`${condition}.value`}
        component={RenderSelect2CreatableV2}
        placeholder="Select value"
        validate={[isRequiredValue]}
        type={'product_types'}
        filterPrefix={''}
      />
    );

    const select2CreatableFieldTag = (
      <Field
        name={`${condition}.value`}
        component={RenderSelect2CreatableV2}
        placeholder="Select value"
        validate={[isRequiredValue]}
        type={'tags'}
        filterPrefix={''}
      />
    );

    const field = {
      '': textField,
      title: textField,
      product_type: select2CreatableFieldProductType,
      vendor: textField,
      price: textField,
      inventory: selectField,
      tags: select2CreatableFieldTag,
      created_at: datePickerField,
      published_at: datePickerField
    };

    return field[productAttribute];
  };

  // Handle change product attribute
  changeProductAttribute = (newAttribute, oldAttribute, index) => {
    const { i18n } = this.props;
    let initialValue = '';
    if (newAttribute === 'inventory') {
      initialValue = VALUES_FOR_PRODUCT_INVENTORY[0].value;
    } else {
      initialValue = _.get(this.getRelationsList(i18n), `${newAttribute}[0].value`, '');
    }

    this.props.changeProductAttribute(index, newAttribute, oldAttribute, initialValue);
  };

  // Handle choose date from picker
  onChooseDate = (selectedDates, index) => {
    const { start } = selectedDates;

    // Save date object to field
    this.props.onChooseDate(start, index);
  };
}

export default withI18n()(ConditionsList);
